// CSS injected once per document so the contenteditable renders
// lists, code blocks, blockquote chrome, and mention chips the way
// Slack's composer does. We inject from JS instead of shipping a
// separate stylesheet so the editor package stays drop-in.
//
// Selector specificity: scoped under `.enriched-text-input` so we
// don't bleed into the rest of the host page.

const STYLE_ID = 'enriched-text-input-styles';

const CSS = `
.enriched-text-input { line-height: 1.45; }
/* Placeholder. The two selectors MUST stay in separate rule blocks: the
   :has() variant covers Safari's contenteditable leaving an empty
   paragraph node (which defeats :empty), but :has() only shipped in
   Safari 15.4. In one comma-separated list an un-parsed :has() on older
   WebKit invalidates the WHOLE rule — dropping the working :empty branch
   too, so the placeholder vanished entirely. Split so each degrades alone. */
.enriched-text-input[data-placeholder]:empty::before {
  content: attr(data-placeholder);
  color: rgba(127, 127, 137, 0.65);
  pointer-events: none;
}
.enriched-text-input[data-placeholder]:not(:focus):has(> p:only-child:empty)::before {
  content: attr(data-placeholder);
  color: rgba(127, 127, 137, 0.65);
  pointer-events: none;
}
.enriched-text-input p,
.enriched-text-input blockquote,
.enriched-text-input ul,
.enriched-text-input ol,
.enriched-text-input pre { margin: 0; }
.enriched-text-input p + p,
.enriched-text-input blockquote + *,
.enriched-text-input ul + *,
.enriched-text-input ol + *,
.enriched-text-input pre + * { margin-top: 4px; }

.enriched-text-input blockquote {
  border-left: 3px solid rgba(127, 127, 137, 0.55);
  padding-left: 10px;
  color: inherit;
}

.enriched-text-input ul,
.enriched-text-input ol {
  padding-left: 22px;
}
.enriched-text-input ul { list-style: disc outside; }
.enriched-text-input ol { list-style: decimal outside; }
.enriched-text-input ul ul,
.enriched-text-input ol ul { list-style: circle outside; }
.enriched-text-input ul ul ul,
.enriched-text-input ol ol ul { list-style: square outside; }
.enriched-text-input ul ol,
.enriched-text-input ol ol { list-style: decimal outside; }
.enriched-text-input li { margin: 0 0 2px 0; }
.enriched-text-input li > ul,
.enriched-text-input li > ol { margin-top: 2px; padding-left: 22px; }

.enriched-text-input code {
  font-family: "SFMono-Regular", ui-monospace, Menlo, Consolas, monospace;
  font-size: 0.92em;
  padding: 1px 4px;
  border-radius: 3px;
  background: rgba(127, 127, 137, 0.18);
}

.enriched-text-input pre {
  position: relative;
  font-family: "SFMono-Regular", ui-monospace, Menlo, Consolas, monospace;
  font-size: 0.92em;
  padding: 8px 10px;
  /* Room for the language chip in the top-right corner. */
  padding-top: 26px;
  border-radius: 6px;
  background: rgba(127, 127, 137, 0.18);
  white-space: pre-wrap;
  word-break: break-word;
}

/* Always-visible language selector chip on every code block. */
.enriched-text-input .ping-code-lang-chip {
  position: absolute;
  top: 4px;
  right: 6px;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  height: 18px;
  padding: 0 7px;
  border-radius: 9px;
  font-family: -apple-system, "Segoe UI", system-ui, sans-serif;
  font-size: 11px;
  font-weight: 600;
  line-height: 18px;
  color: rgba(180, 186, 195, 0.95);
  background: rgba(127, 127, 137, 0.22);
  cursor: pointer;
  user-select: none;
  -webkit-user-select: none;
}
.enriched-text-input .ping-code-lang-chip:hover {
  background: rgba(83, 126, 255, 0.28);
  color: #fff;
}
.enriched-text-input pre code {
  background: transparent;
  padding: 0;
  border-radius: 0;
}

.enriched-text-input a {
  color: #537EFF;
  text-decoration: underline;
}

.enriched-text-input .mention-chip {
  display: inline-block;
  padding: 0 4px;
  border-radius: 4px;
  background: rgba(83, 126, 255, 0.18);
  color: #537EFF;
  font-weight: 600;
  /* Make the chip atomic - clicking selects it whole, Backspace
     deletes it in one step (Slack-style). The contenteditable=false
     attribute on the element does most of the work; the cursor +
     caret rules here just make it feel right. */
  cursor: pointer;
  user-select: all;
}
.enriched-text-input .mention-chip:hover {
  background: rgba(83, 126, 255, 0.28);
}

.enriched-text-input .image-paste-preview {
  display: inline-block;
  vertical-align: middle;
  max-height: 80px;
  max-width: 160px;
  margin: 2px 4px;
  border-radius: 6px;
  border: 1px solid rgba(127, 127, 137, 0.35);
}
`;

let injected = false;

/**
 * Insert the editor's stylesheet into the document head, exactly
 * once per page. Safe to call from `useLayoutEffect` mount; no-ops
 * on subsequent calls.
 */
export function ensureEditorStyles(doc: Document = document): void {
  if (injected) return;
  if (doc.getElementById(STYLE_ID) !== null) {
    injected = true;
    return;
  }
  const style = doc.createElement('style');
  style.id = STYLE_ID;
  style.textContent = CSS;
  doc.head.appendChild(style);
  injected = true;
}
